import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Play, Volume2, VolumeX } from "lucide-react";
import MagneticButton from "../components/MagneticButton";
import { useI18n } from "../i18n/I18nProvider";

const FOUNDER_HERO = "/generated/founder_cinema_camera.jpg";
const SHOWREEL_CLIPS = [
  "/generated/videos/atlantic-craft.mp4",
  "/generated/videos/fundy-aerials.mp4",
  "/generated/videos/rouge-founder.mp4",
  "/generated/videos/cafe-social.mp4",
];

export default function Hero() {
  const { t, lang } = useI18n();
  const ref = useRef(null);
  const videoRef = useRef(null);
  const [clipIdx, setClipIdx] = useState(0);
  const [videoReady, setVideoReady] = useState(false);
  const [muted, setMuted] = useState(true);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  // Respect prefers-reduced-motion: skip the autoplay reel entirely.
  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    const mq = typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(prefers-reduced-motion: reduce)") : null;
    if (mq) {
      setReducedMotion(mq.matches);
      const h = (e) => setReducedMotion(e.matches);
      mq.addEventListener?.("change", h);
      return () => mq.removeEventListener?.("change", h);
    }
  }, []);

  // When clip index changes, force <video> to load the new src and play.
  useEffect(() => {
    if (reducedMotion) return;
    const v = videoRef.current;
    if (!v) return;
    v.load();
    const p = v.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
  }, [clipIdx, reducedMotion]);

  const onEnded = () => setClipIdx((i) => (i + 1) % SHOWREEL_CLIPS.length);
  const onCanPlay = () => setVideoReady(true);

  const reveal = {
    hidden: { y: "110%" },
    show: (i = 0) => ({
      y: 0,
      transition: { duration: 1.05, ease: [0.22, 1, 0.36, 1], delay: 0.2 + i * 0.12 },
    }),
  };

  return (
    <section ref={ref} className="relative min-h-[100vh] flex flex-col justify-end pb-16 lg:pb-24 pt-32 overflow-hidden" data-testid="home-hero">
      {/* eyebrow band */}
      <div className="absolute top-28 left-0 right-0 z-20 mx-auto max-w-[1440px] px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="flex items-center gap-3 text-[11px] tracking-[0.28em] uppercase text-ink-300"
        >
          <span className="block w-8 h-px bg-rouge" />
          <span>{t.hero.eyebrow}</span>
        </motion.div>
      </div>

      {/* showreel right side: video on top of static poster, both behind dark gradient */}
      <motion.div
        style={{ y: yImg, scale: scaleImg }}
        className="absolute right-0 top-0 bottom-0 w-full lg:w-[52%] z-10 duotone-overlay"
        data-testid="hero-showreel"
      >
        {/* Poster (always present, acts as fallback) */}
        <img
          src={FOUNDER_HERO}
          alt="Phaneroo Prod founder operating a RED cinema camera"
          className="absolute inset-0 w-full h-full object-cover object-[55%_30%]"
        />
        {/* Auto-playing AI Sora 2 showreel */}
        {!reducedMotion && (
          <motion.video
            ref={videoRef}
            key={clipIdx}
            src={SHOWREEL_CLIPS[clipIdx]}
            autoPlay
            muted={muted}
            playsInline
            preload="auto"
            onEnded={onEnded}
            onCanPlay={onCanPlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: videoReady ? 1 : 0 }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            aria-hidden="true"
            data-testid="hero-showreel-video"
            className="absolute inset-0 w-full h-full object-cover object-[55%_30%]"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/55 to-transparent lg:from-[#050505]/95 lg:via-[#050505]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
        {!reducedMotion && videoReady && (
          <button
            type="button"
            onClick={() => {
              const v = videoRef.current;
              if (!v) return;
              const next = !muted;
              setMuted(next);
              v.muted = next;
              if (!next) {
                const p = v.play();
                if (p && typeof p.catch === "function") p.catch(() => setMuted(true));
              }
            }}
            aria-label={muted ? (lang === "fr" ? "Activer le son" : "Unmute") : (lang === "fr" ? "Couper le son" : "Mute")}
            data-testid="hero-sound-toggle"
            className="absolute bottom-6 right-6 z-30 flex items-center gap-2 bg-black/60 hover:bg-black/80 backdrop-blur border border-white/15 hover:border-rouge px-3 py-2 text-[10px] tracking-[0.22em] uppercase text-ink-100 transition-colors"
          >
            {muted ? <VolumeX size={13} /> : <Volume2 size={13} className="text-rouge" />}
            {muted ? (lang === "fr" ? "Son" : "Sound") : (lang === "fr" ? "Couper" : "Mute")}
          </button>
        )}
      </motion.div>

      {/* TITLE */}
      <div className="relative z-20 mx-auto w-full max-w-[1440px] px-6 lg:px-10">
        <h1 className="massive-display text-ink-100" data-testid="hero-title">
          {[t.hero.title_a, t.hero.title_b, t.hero.title_c].map((w, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span variants={reveal} initial="hidden" animate="show" custom={i} className="block">
                {i === 1 ? <em className="italic font-light text-rouge/95">{w}</em> : w}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-10 lg:mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85 }}
            className="lg:col-span-5 text-ink-300 text-base lg:text-lg leading-relaxed max-w-md"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.0 }}
            className="lg:col-span-7 flex flex-wrap items-center gap-4"
          >
            <MagneticButton
              as={Link}
              to="/contact"
              data-testid="hero-cta-quote"
              data-cursor={t.hero.cta_quote}
              className="group inline-flex items-center gap-3 bg-rouge hover:bg-rouge-deep text-ink-100 px-7 py-4 text-[11px] tracking-[0.24em] uppercase transition-colors"
            >
              {t.hero.cta_quote}
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </MagneticButton>
            <MagneticButton
              as={Link}
              to="/portfolio"
              data-testid="hero-cta-reel"
              data-cursor={t.hero.cta_reel}
              className="group inline-flex items-center gap-3 border border-ink-500 hover:border-ink-100 text-ink-100 px-7 py-4 text-[11px] tracking-[0.24em] uppercase transition-colors"
            >
              <Play size={14} className="text-rouge" />
              {t.hero.cta_reel}
            </MagneticButton>
          </motion.div>
        </div>

        {/* stats strip */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="mt-16 lg:mt-24 grid grid-cols-3 gap-6 border-t border-hair pt-8 max-w-2xl"
        >
          {[
            { n: "120+", l: t.hero.stat_1 },
            { n: "08", l: t.hero.stat_2 },
            { n: "04", l: t.hero.stat_3 },
          ].map((s) => (
            <div key={s.l}>
              <div className="font-serif-editorial text-3xl lg:text-4xl text-ink-100 leading-none">{s.n}</div>
              <div className="mt-2 text-[10px] tracking-[0.22em] uppercase text-ink-500 max-w-[150px]">{s.l}</div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* scroll cue */}
      <div className="absolute bottom-8 right-8 z-20 hidden md:flex flex-col items-center gap-3" aria-hidden>
        <span className="text-[10px] tracking-[0.3em] uppercase text-ink-500">{lang === "fr" ? "Faites défiler" : "Scroll"}</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity }}
          className="w-px h-10 bg-ink-500"
        />
      </div>
    </section>
  );
}
